/**
 * TypeScript type definitions for the Data Requirements feature.
 *
 * Data Requirements are specific pieces of information expected by a Task to meet its completion criteria.
 * This system allows for:
 * - Systematic tracking of what data has been extracted vs. what's still missing
 * - Automatic extraction of values from artifacts by agents
 * - Human review and approval of extracted values
 * - Integration with canonical models (Matter, Client, etc.) for existing values
 */

import { AgentType } from './agent-types';

// ==================== Enums ====================

/**
 * Canonical names for data requirements.
 * These represent standardized data points that can be extracted from legal documents.
 */
export enum DataRequirementName {
  /** Client's full legal name */
  CLIENT_NAME = 'client_name',
  /** Client's date of birth */
  CLIENT_DATE_OF_BIRTH = 'client_date_of_birth',
  /** Last four digits of client's Social Security Number */
  CLIENT_LAST_FOUR_OF_SSN = 'client_last_four_of_ssn',
  /** Client's current address */
  CLIENT_ADDRESS = 'client_address',

  /** Date when the incident/loss occurred */
  INCIDENT_DATE_OF_LOSS = 'date_of_loss',
  /** Location where the incident occurred */
  INCIDENT_LOCATION = 'incident_location',
  /** Description of what happened during the incident */
  INCIDENT_DESCRIPTION = 'incident_description',

  /** Insurance policy number */
  INSURANCE_POLICY_NUMBER = 'insurance_policy_number',
  /** Insurance claim number */
  INSURANCE_CLAIM_NUMBER = 'insurance_claim_number',
  /** Name of the insurance adjuster */
  INSURANCE_ADJUSTER_NAME = 'insurance_adjuster_name',

  /** Name of medical provider/facility */
  MEDICAL_PROVIDER_NAME = 'medical_provider_name',
  /** Date of medical treatment */
  MEDICAL_TREATMENT_DATE = 'medical_treatment_date',
}

/**
 * Models that may store canonical values for data requirements.
 * These represent existing database entities that might already contain the required data.
 */
export enum CanonicalModel {
  /** Matter model - case/legal matter information */
  MATTER = 'matter',
  /** Client model - client demographic and contact information */
  CLIENT = 'client',
  /** Insurance coverage model - policy and coverage details */
  INSURANCE_COVERAGE = 'insurance_coverage',
  /** Medical provider model - healthcare provider information */
  MEDICAL_PROVIDER = 'medical_provider',
  /** Opposing party model - defendant/opposing counsel information */
  OPPOSING_PARTY = 'opposing_party',
}

/**
 * Status of a data requirement.
 */
export enum DataRequirementStatus {
  /** Waiting for a value to be extracted or provided */
  PENDING = 'Pending',
  /** At least one approved value exists or canonical model has value */
  SATISFIED = 'Satisfied',
  /** The requirement was bypassed by the user */
  BYPASSED = 'Bypassed',
}

/**
 * Status of a specific data requirement value.
 */
export enum DataRequirementValueStatus {
  /** Value was extracted by an agent and needs human review */
  EXTRACTED = 'Extracted',
  /** Value was approved by a human or extracted by a human */
  APPROVED = 'Approved',
  /** Value was rejected during review */
  REJECTED = 'Rejected',
}

// ==================== Context and Criteria Interfaces ====================

/**
 * Context explaining why a data requirement was created.
 */
export interface DataRequirementAgentContext {
  /** Explanation of why this data requirement is needed */
  rationale?: string | null;
}

/**
 * Validation criteria for a data requirement.
 * Defines how the extracted data should be formatted and validated.
 */
export interface DataRequirementValidationCriteria {
  /** Expected format (e.g., "YYYY-MM-DD", "phone number", "email") */
  expected_format?: string | null;
  /** Custom validation instructions for reviewers */
  validation_prompt?: string | null;
  /** Required fields for complex data types */
  required_fields?: string[] | null;
}

/**
 * Context for a data requirement value.
 * Provides additional information about how/where the value was obtained.
 */
export interface DataRequirementValueContext {
  /** Explanation of why this value was extracted */
  rationale?: string | null;
  /** Which artifact/document the value came from */
  extraction_source?: string | null;
  /** Notes about confidence level or extraction quality */
  confidence_notes?: string | null;
  /** ID of the artifact this value was extracted from */
  artifact_id?: string | null;
  /** Relevant text excerpts that support the extraction */
  excerpts?: string[] | null;
  /** Additional context about the extraction process */
  extraction_context?: string | null;
  /** Citations to specific locations in the source document */
  citation_ids?: string[] | null;
}

// ==================== Base Schemas ====================

/**
 * Base properties shared by all data requirement schemas.
 */
export interface BaseDataRequirement {
  /** Canonical name for the data requirement */
  name: DataRequirementName;
  /** Human-readable name for display in UI */
  display_name: string;
  /** Optional description of what this data requirement is for */
  description?: string | null;
  /** Natural language prompt given to LLM to extract this information */
  prompt: string;
  /** Validation criteria for the extracted data */
  validation_criteria?: DataRequirementValidationCriteria | null;
  /** The canonical model that may already store this value */
  canonical_model?: CanonicalModel | null;
  /** The field name in the canonical model that stores this value */
  canonical_field?: string | null;
  /** Current status of the data requirement */
  status?: DataRequirementStatus | null;
}

/**
 * Base properties for data requirement values.
 */
export interface BaseDataRequirementValue {
  /** The extracted or provided value (always stored as string) */
  value: string;
  /** Status of this value (extracted, approved, rejected) */
  status?: DataRequirementValueStatus | null;
  /** Agent-issued confidence in the extracted value (0.0 - 1.0) */
  confidence?: number | null;
  /** Additional context about the extraction */
  context?: DataRequirementValueContext | null;
}

// ==================== CRUD Schemas ====================

/**
 * Schema for creating a new data requirement.
 */
export interface CreateDataRequirement extends BaseDataRequirement {
  /** ID of the task this requirement belongs to */
  task_id: string;
}

/**
 * Schema for updating an existing data requirement.
 * All fields are optional for partial updates.
 */
export type UpdateDataRequirement = Partial<BaseDataRequirement>;

/**
 * Schema for updating only the status of a data requirement.
 */
export interface UpdateDataRequirementStatus {
  /** New status for the data requirement */
  status: DataRequirementStatus;
}

/**
 * Schema for deleting a data requirement.
 */
export interface DeleteDataRequirement {
  /** ID of the data requirement to delete */
  id: string;
}

/**
 * Template for a data requirement.
 * Used as a reference for prompts to construct bespoke requirements.
 */
export type DataRequirementTemplate = BaseDataRequirement;

// ==================== Full Data Requirement Schema ====================

/**
 * Complete data requirement with all metadata and related values.
 * This is the main interface returned by GET endpoints.
 */
export interface DataRequirement extends BaseDataRequirement {
  /** Unique identifier for the data requirement */
  id: string;
  /** ID of the task this requirement belongs to */
  task_id: string;
  /** When the requirement was created */
  created_at: string;
  /** When the requirement was last modified */
  modified_at: string;
  /** User who created the requirement (if created by human) */
  user_id?: string | null;
  /** Agent who created the requirement (if created by agent) */
  agent?: AgentType | null;
  /** Context explaining why the requirement was created */
  agent_context?: DataRequirementAgentContext | null;
  /** All values that have been extracted/provided for this requirement */
  values: DataRequirementValue[];
  /** Value from canonical model if it exists */
  canonical_value?: string | null;
}

// ==================== Data Requirement Value CRUD Schemas ====================

/**
 * Schema for creating a new data requirement value.
 */
export interface CreateDataRequirementValue extends BaseDataRequirementValue {
  /** ID of the data requirement this value satisfies */
  data_requirement_id: string;
}

/**
 * Schema for updating an existing data requirement value.
 * All fields are optional for partial updates.
 */
export type UpdateDataRequirementValue = Partial<BaseDataRequirementValue>;

/**
 * Schema for deleting a data requirement value.
 */
export interface DeleteDataRequirementValue {
  /** ID of the value to delete */
  id: string;
}

// ==================== Full Data Requirement Value Schema ====================

/**
 * Complete data requirement value with all metadata.
 */
export interface DataRequirementValue extends BaseDataRequirementValue {
  /** Unique identifier for the value */
  id: string;
  /** ID of the data requirement this value satisfies */
  data_requirement_id: string;
  /** When the value was created */
  created_at: string;
  /** When the value was last modified */
  modified_at: string;
  /** User who provided or reviewed this value */
  user_id?: string | null;
  /** Agent who extracted this value */
  agent?: AgentType | null;
  /** Required status for complete data requirement values */
  status: DataRequirementValueStatus;
}

// ==================== Summary and Collection Types ====================

/**
 * Summary statistics for data requirements.
 */
export interface DataRequirementSummary {
  /** Total number of data requirements */
  total: number;
  /** Number of pending data requirements */
  pending: number;
  /** Number of satisfied data requirements */
  satisfied: number;
  /** Number of bypassed data requirements */
  bypassed: number;
}

/**
 * Stub version of data requirement for list views.
 * Contains essential information without full relationship data.
 */
export interface DataRequirementStub {
  /** Unique identifier */
  id: string;
  /** Canonical name */
  name: DataRequirementName;
  /** Display name */
  display_name: string;
  /** Current status */
  status?: DataRequirementStatus | null;
  /** When requirement was satisfied */
  satisfied_at?: string | null;
  /** Summary of values */
  values: DataRequirementValueStub[];
}

/**
 * Stub version of data requirement value for list views.
 */
export interface DataRequirementValueStub {
  /** Unique identifier */
  id: string;
  /** The value */
  value: string;
  /** Status */
  status: DataRequirementValueStatus;
  /** Confidence score */
  confidence?: number | null;
  /** Agent that extracted it */
  agent?: AgentType | null;
}

// ==================== Batch Operation Types ====================

/**
 * Response from batch creating data requirements.
 */
export interface BatchCreateDataRequirementsResponse {
  /** Successfully created requirements */
  created: DataRequirement[];
  /** Any errors that occurred */
  errors?: Array<{
    /** Index in the input array where error occurred */
    index: number;
    /** Error message */
    error: string;
  }>;
}

/**
 * Response from batch extracting data requirement values.
 */
export interface BatchExtractDataRequirementValuesResponse {
  /** Successfully extracted values */
  extracted: DataRequirementValue[];
  /** Any errors that occurred */
  errors?: Array<{
    /** Index in the input array where error occurred */
    index: number;
    /** Error message */
    error: string;
  }>;
}

// ==================== API Error Types ====================

/**
 * Standard API error response.
 */
export interface DataRequirementApiError {
  /** Error message */
  error: string;
  /** Detailed validation errors if applicable */
  details?: Array<{
    /** Field that failed validation */
    field: string;
    /** Error message for the field */
    message: string;
  }>;
}
